import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Loader2,
  Clock,
  AlertTriangle,
  LogOut,
  CheckCircle2,
  UserX,
  ShieldCheck,
  FileText,
} from 'lucide-react';

import { StickyNoteCard } from '@/components/StickyNoteCard';
import { ModalShell } from '@/components/ModalShell';
import { ProgressModal } from '@/components/progress/ProgressModal';
import { UploaderScreeningDetailModal } from '@/pages/uploader/components/UploaderScreeningDetailModal';
import { useAgentCard, type AgentCardScreening, type AgentCardView } from '@/features/admin/agentCard.api';
import { useT, useLang } from '@/i18n';
import { cn } from '@/lib/utils';

/**
 * F5-R6a — «Ficha de agente» (dibujo del CEO, Monitoreo v2 · 2026-08-29).
 *
 * El modal que abre una tarjeta del piso (Monitoreo en vivo) y la tabla por
 * agente de Reportes. Contra la versión vieja: UNA tabla métrica×período en
 * vez de cuatro tarjetas con las mismas seis etiquetas, personas en vez de
 * hashes, «Ahora mismo» arriba en vez de «LIVE STATE» al fondo, y todo en un
 * idioma. Datos: GET /admin/agents/:id/card (nuevo, solo lectura — muestra
 * nombres de miembros, así que el server AUDITA cada apertura).
 *
 * MUEREN del modal viejo (todos vivos-no-dibujados, con firma que los cubre):
 *   · «Edit target» (PATCH :id/target) — el override individual vive en Team
 *     (decisión T1a); acá la ficha es de consulta, «ninguna acción sobre el
 *     agente se ejecuta desde acá».
 *   · los chips de bucket under_8/8_to_22/… — el dibujo los elimina («una
 *     clave interna de rango de duración» al lado del minuto exacto).
 *   · «In queue N pending · N active» y «Tab focus» — no dibujados.
 *   · la fila de guiones y el «pending» en inglés.
 * SE CONSERVAN (E-23, vivos no dibujados): click en la fila abre el detalle
 * read-only del screening · el botón de notas expande las notas por dominio.
 *
 * Desviaciones declaradas (tokens): #fafbfc→nav-softer · #9aa7b2→nav-faint ·
 * #7a5410→nav-amberText · #3c5f3c→nav-sageText · #3f7f81→nav-tealDark ·
 * #e8f3f3/#f4fafa/#c9e2e2 literales (sin token; precedente border-[#c9e2e2]
 * en F5 shippeado). El «modal crece con la página» del artefacto se rinde
 * como header pegado + cuerpo con el scroll del chasis (8 px visibles) — el
 * chasis ModalShell no se toca.
 */

type LogFilter = 'all' | 'pending' | 'flagged';
type LiveState = AgentCardView['live']['state'];

interface AgentDetailModalProps {
  agentId: string | null;
  onClose: () => void;
}

/** Tonos del dibujo: teal en llamada · ámbar sin actividad · salvia
 *  disponible (4to estado real, no dibujado — declarado) · neutro
 *  desconectado («no es un problema y no se pinta como tal»). */
const TONE: Record<
  LiveState,
  { card: string; chip: string; dot: string; grid: string; icon: string; btn: string }
> = {
  call: {
    card: 'bg-nav-tealTint border-nav-teal text-nav-tealDark',
    chip: 'bg-nav-tealTint border-nav-teal text-nav-tealDark',
    dot: 'bg-nav-teal',
    grid: 'border-nav-teal',
    icon: 'text-nav-tealDark',
    btn: 'border-nav-teal text-nav-tealDark',
  },
  idle: {
    card: 'bg-nav-amberTint border-nav-amber text-nav-amberText',
    chip: 'bg-nav-amberTint border-nav-amber text-nav-amberText',
    dot: 'bg-nav-amber',
    grid: 'border-nav-amber',
    icon: 'text-nav-amberText',
    btn: 'border-nav-amber text-nav-amberText',
  },
  available: {
    card: 'bg-nav-sageTint border-nav-sage text-nav-sageText',
    chip: 'bg-nav-sageTint border-nav-sage text-nav-sageText',
    dot: 'bg-nav-sageMid',
    grid: 'border-nav-sage',
    icon: 'text-nav-sageText',
    btn: 'border-nav-sage text-nav-sageText',
  },
  off: {
    card: 'bg-nav-hair border-nav-card text-nav-slate',
    chip: 'bg-nav-hair border-nav-card text-nav-slate',
    dot: 'bg-nav-faint',
    grid: 'border-nav-card',
    icon: 'text-nav-slate',
    btn: 'border-nav-line text-nav-slate',
  },
};

const GHOST_BTN =
  'inline-flex items-center min-h-[44px] px-[15px] bg-white text-nav-navy border-[1.5px] border-nav-line rounded-lg font-head text-[12.5px] font-bold transition-colors duration-150 motion-reduce:transition-none hover:bg-[#f4fafa] hover:border-nav-teal hover:text-nav-tealDark active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/** h:mm del dibujo — «1:12», «8:51»; la unidad la dice la aclaración de la fila. */
function fmtHM(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}`;
}

function hhmm(iso: string | null, lang: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString(lang === 'es' ? 'es' : 'en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function AgentDetailModal({ agentId, onClose }: AgentDetailModalProps): JSX.Element | null {
  const t = useT();
  const lang = useLang();
  const navigate = useNavigate();
  const cardQ = useAgentCard(agentId);

  const containerRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLHeadingElement>(null);

  const [logFilter, setLogFilter] = useState<LogFilter>('all');
  const [selectedScreeningId, setSelectedScreeningId] = useState<string | null>(null);
  const [progressOpen, setProgressOpen] = useState(false);

  // A11y del dibujo: el foco atrapado arranca en el nombre.
  const card = cardQ.data;
  useEffect(() => {
    if (!agentId || !card) return;
    nameRef.current?.focus();
  }, [agentId, card]);

  useEffect(() => {
    // Al cambiar de agente, la ficha arranca limpia.
    setLogFilter('all');
    setSelectedScreeningId(null);
    setProgressOpen(false);
  }, [agentId]);

  const filteredLog = useMemo(() => {
    const rows = card?.recent ?? [];
    if (logFilter === 'all') return rows;
    return rows.filter((r) => r.findhelpStatus === logFilter);
  }, [card, logFilter]);

  if (!agentId) return null;

  // Foco atrapado dentro de la ficha (Tab cicla; Escape lo maneja el chasis).
  const trapFocus = (e: React.KeyboardEvent): void => {
    if (e.key !== 'Tab' || !containerRef.current) return;
    const focusables = Array.from(
      containerRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const goAndClose = (to: string): void => {
    onClose();
    navigate(to);
  };

  const deactivated = card != null && !card.identity.active;
  const state: LiveState = deactivated ? 'off' : (card?.live.state ?? 'off');
  const tone = TONE[state];

  const liveChipLabel = card
    ? deactivated
      ? card.identity.deactivatedAt
        ? t('f5r6a.deac.chip', {
            d: new Date(card.identity.deactivatedAt).toLocaleDateString(lang === 'es' ? 'es' : 'en', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            }),
          })
        : t('f5r6a.state.deactivated')
      : t(`f5r6a.state.${state}` as 'f5r6a.state.call')
    : '';

  return (
    <ModalShell open={agentId !== null} onClose={onClose} ariaLabelledBy="agent-card-name" widthClass="max-w-[860px]">
      <div
        ref={containerRef}
        onKeyDown={trapFocus}
        className="flex flex-col flex-1 min-h-0 font-body text-nav-navy animate-fade-up-slow motion-reduce:animate-none"
      >
        {/* ============ Encabezado pegado — 84px, avatar 48 ============ */}
        <header className="sticky top-0 z-10 min-h-[84px] flex items-center gap-3.5 flex-wrap bg-white border-b-[1.5px] border-nav-card pl-[26px] pr-[22px] py-[18px] rounded-t-[14px]">
          <span
            aria-hidden
            className={cn(
              'flex-none inline-flex items-center justify-center w-12 h-12 rounded-full text-white font-head text-[16px] font-bold',
              deactivated || state === 'off' ? 'bg-nav-faint' : 'bg-nav-navy',
            )}
          >
            {card ? initials(card.identity.fullName || card.identity.email) : '·'}
          </span>

          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-[9px] flex-wrap mb-1.5">
              <h2
                id="agent-card-name"
                ref={nameRef}
                tabIndex={-1}
                className="m-0 font-head text-[21px] font-extrabold tracking-[-0.02em] focus-visible:outline-none"
              >
                {card?.identity.fullName ?? t('f5r6a.loading')}
              </h2>
              {card && (
                <span className="bg-nav-tealTint text-nav-tealDark border border-[#c9e2e2] rounded-full px-2.5 py-[3px] font-head text-[10.5px] font-bold">
                  {card.identity.role === 'agent' ? t('f5r6a.role.agent') : card.identity.role}
                </span>
              )}
              {card && card.identity.languages.length > 0 && (
                /* El dibujo dice «Interfaz en español»; el idioma de interfaz
                   no se persiste por usuario en el server (NECESITA-SERVER) —
                   el chip muestra los idiomas reales del agente. */
                <span className="bg-nav-hair text-nav-slate border border-nav-card rounded-full px-2.5 py-[3px] font-head text-[10.5px] font-bold">
                  {card.identity.languages
                    .map((l) => (l === 'es' ? t('f5r6a.lang.es') : l === 'en' ? t('f5r6a.lang.en') : l))
                    .join(' · ')}
                </span>
              )}
            </div>
            {/* El «turno 06:00–14:00 · Bronx» del dibujo no existe como dato
                (NECESITA-SERVER: no hay turnos en el sistema) — email solo. */}
            <p className="m-0 text-[12.5px] text-nav-slate">{card?.identity.email ?? ''}</p>
          </div>

          {card && (
            <span
              aria-live="polite"
              className={cn(
                'inline-flex items-center gap-2 flex-none rounded-full border-[1.5px] px-3.5 py-2 font-head text-[12px] font-bold whitespace-nowrap',
                tone.chip,
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'w-2 h-2 rounded-full',
                  tone.dot,
                  state === 'call' && !deactivated && 'animate-pulse motion-reduce:animate-none',
                )}
              />
              {liveChipLabel}
            </span>
          )}

          <button
            type="button"
            onClick={onClose}
            aria-label={t('f5r6a.close')}
            className="flex-none inline-flex items-center justify-center w-11 h-11 rounded-lg text-nav-slate transition-colors duration-150 motion-reduce:transition-none hover:bg-nav-soft hover:text-nav-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
          >
            <X className="h-[18px] w-[18px]" aria-hidden />
          </button>
        </header>

        {/* ============ Cuerpo — scroll del chasis (8px visibles) ============ */}
        <div className="modal-scroll flex-1 overflow-y-auto">
          <div className="px-[26px] pt-[22px] pb-[26px]">
            {cardQ.isLoading && !card && (
              <div className="text-center py-10">
                <Loader2 className="h-5 w-5 animate-spin motion-reduce:animate-none mx-auto text-nav-teal" aria-hidden />
                <p className="mt-2 text-[13px] text-nav-slate">{t('f5r6a.loading')}</p>
              </div>
            )}

            {cardQ.error && !card && (
              <div className="border-[1.5px] border-[#e3b5ae] bg-nav-redTint rounded-xl p-4">
                <p className="m-0 text-sm text-nav-red">
                  {t('f5r6a.error', { msg: (cardQ.error as Error).message })}
                </p>
              </div>
            )}

            {card && (
              <>
                <NowCard
                  card={card}
                  state={state}
                  deactivated={deactivated}
                  lang={lang}
                  onOpenScreening={(id) => setSelectedScreeningId(id)}
                  onReturnToQueue={() => goAndClose('/admin/reassign')}
                />

                <PeriodTable card={card} lang={lang} />

                {card.logTotal === 0 ? (
                  /* Estado propio del dibujo: agente sin screenings — salvia,
                     sin botón. */
                  <div className="border-[1.5px] border-nav-sage bg-nav-sageTint rounded-xl px-5 py-6 text-center">
                    <p className="m-0 font-head text-[14px] font-bold text-nav-sageText">
                      {t('f5r6a.empty.title')}
                    </p>
                    <p className="m-0 mt-1.5 text-[13px] text-nav-sageText/90">{t('f5r6a.empty.body')}</p>
                  </div>
                ) : (
                  <ScreeningsLog
                    card={card}
                    rows={filteredLog}
                    filter={logFilter}
                    onFilter={setLogFilter}
                    lang={lang}
                    onOpenScreening={(id) => setSelectedScreeningId(id)}
                    onSeeAll={() => goAndClose('/admin/reports')}
                  />
                )}

                {/* ============ Pie: sólo lectura + las dos salidas ============ */}
                <div className="flex items-center gap-2.5 flex-wrap mt-[22px] pt-[18px] border-t border-nav-hair">
                  <span className="inline-flex items-center gap-2 font-body text-[12.5px] text-nav-slate">
                    <ShieldCheck className="h-[15px] w-[15px] flex-none" aria-hidden />
                    {t('f5r6a.foot.readOnly')}
                  </span>
                  <span className="flex-1" />
                  {/* Agente desactivado: la ficha queda en gris y SIN las dos
                      salidas de acción (estado propio del dibujo). */}
                  {!deactivated && (
                    <>
                      <button type="button" className={GHOST_BTN} onClick={() => setProgressOpen(true)}>
                        {t('f5r6a.foot.progress')}
                      </button>
                      <button type="button" className={GHOST_BTN} onClick={() => goAndClose('/admin/reassign')}>
                        {t('f5r6a.foot.reassign')}
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {selectedScreeningId && (
        <UploaderScreeningDetailModal
          screeningId={selectedScreeningId}
          onClose={() => setSelectedScreeningId(null)}
          readOnly
        />
      )}
      {/* «Ver su progreso» → la ventana de niveles y metas existente. */}
      <ProgressModal
        open={progressOpen}
        onClose={() => setProgressOpen(false)}
        mode="admin"
        agentId={agentId != null && Number.isFinite(Number(agentId)) ? Number(agentId) : undefined}
      />
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// «Ahora mismo» — la tarjeta que sube al tope: el estado en una frase + la
// grilla de los tres campos técnicos.
// ---------------------------------------------------------------------------

function NowCard({
  card,
  state,
  deactivated,
  lang,
  onOpenScreening,
  onReturnToQueue,
}: {
  card: AgentCardView;
  state: LiveState;
  deactivated: boolean;
  lang: string;
  onOpenScreening: (id: string) => void;
  onReturnToQueue: () => void;
}): JSX.Element {
  const t = useT();
  const tone = TONE[state];
  const lv = card.live;

  const hbLabel = (iso: string | null): string => {
    if (!iso) return t('f5r6a.now.hb.never');
    const diffSec = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (diffSec < 60) return t('f5r6a.now.hb.seconds', { n: diffSec });
    if (diffSec < 3600) return t('f5r6a.now.hb.minutes', { n: Math.round(diffSec / 60) });
    return t('f5r6a.now.hb.today', { t: hhmm(iso, lang) });
  };

  const screenLabel = (): string => {
    if (deactivated || state === 'off') return t('f5r6a.screen.loggedOut');
    if (lv.currentScreeningId) {
      return lv.domainsAnswered && lv.domainsAnswered > 0
        ? t('f5r6a.screen.screening', { n: Math.min(9, lv.domainsAnswered) })
        : t('f5r6a.screen.screeningStart');
    }
    const p = lv.pageUrl ?? '';
    if (p.includes('precall')) return t('f5r6a.screen.precall');
    if (p.includes('queue')) return t('f5r6a.screen.queue');
    if (p.includes('booking')) return t('f5r6a.screen.bookings');
    return p || '—';
  };

  const assignLabel = lv.currentAssignmentId
    ? `#${lv.currentAssignmentId}${lv.currentClientName ? ` · ${lv.currentClientName}` : ''}`
    : t('f5r6a.now.none');

  const callMinutes = lv.currentScreeningSeconds != null ? Math.max(1, Math.round(lv.currentScreeningSeconds / 60)) : null;
  const idleMinutes = lv.lastHeartbeatAt
    ? Math.max(1, Math.round((Date.now() - new Date(lv.lastHeartbeatAt).getTime()) / 60_000))
    : null;

  let Icon = LogOut;
  let title: string;
  let body: string;
  let action: { label: string; onClick: () => void } | null = null;

  if (deactivated) {
    Icon = UserX;
    title = t('f5r6a.now.deacTitle');
    body = card.identity.deactivatedAt
      ? t('f5r6a.now.deacBody', {
          d: new Date(card.identity.deactivatedAt).toLocaleDateString(lang === 'es' ? 'es' : 'en', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          }),
        })
      : t('f5r6a.now.deacBodyNoDate');
  } else if (state === 'call') {
    Icon = Clock;
    title = lv.currentClientName
      ? t('f5r6a.now.callTitle', { name: lv.currentClientName })
      : t('f5r6a.now.callTitleNoName');
    body =
      lv.domainsAnswered && lv.domainsAnswered > 0
        ? t('f5r6a.now.callBody', { m: callMinutes ?? 1, n: Math.min(9, lv.domainsAnswered) })
        : t('f5r6a.now.callBodyStart', { m: callMinutes ?? 1 });
    if (lv.currentScreeningId) {
      const sid = lv.currentScreeningId;
      action = { label: t('f5r6a.now.callAction'), onClick: () => onOpenScreening(sid) };
    }
  } else if (state === 'idle') {
    Icon = AlertTriangle;
    title = idleMinutes != null ? t('f5r6a.now.idleTitle', { m: idleMinutes }) : t('f5r6a.now.idleTitleNoTime');
    body = lv.currentAssignmentId
      ? t('f5r6a.now.idleBody', { id: lv.currentAssignmentId, t: hhmm(lv.lastHeartbeatAt, lang) })
      : t('f5r6a.now.idleBodyNoAssign', { t: hhmm(lv.lastHeartbeatAt, lang) });
    if (lv.currentAssignmentId) {
      /* «Devolver a la cola» del dibujo: acá no se ejecuta nada (ficha de
         consulta) — lleva a /admin/reassign, donde el caso se devuelve de
         verdad. Declarado en el reporte. */
      action = { label: t('f5r6a.now.idleAction'), onClick: onReturnToQueue };
    }
  } else if (state === 'available') {
    Icon = CheckCircle2;
    title = t('f5r6a.now.availTitle');
    body = lv.loginAt
      ? t('f5r6a.now.availBody', { t: hhmm(lv.loginAt, lang) })
      : t('f5r6a.now.availBodyNoTime');
  } else {
    Icon = LogOut;
    title = lv.lastHeartbeatAt
      ? t('f5r6a.now.offTitle', { t: hhmm(lv.lastHeartbeatAt, lang) })
      : t('f5r6a.now.offTitleNoTime');
    body = lv.currentAssignmentId
      ? t('f5r6a.now.offBodyAssign', { id: lv.currentAssignmentId })
      : t('f5r6a.now.offBody');
  }

  const fields: Array<[string, string]> = [
    [t('f5r6a.now.f.assignment'), assignLabel],
    [t('f5r6a.now.f.heartbeat'), hbLabel(lv.lastHeartbeatAt)],
    [t('f5r6a.now.f.screen'), screenLabel()],
  ];

  return (
    <div className={cn('rounded-xl border-[1.5px] px-5 py-[18px] mb-6', tone.card)}>
      <div className="flex items-start gap-3.5 flex-wrap">
        <span className={cn('flex-none inline-flex items-center justify-center w-9 h-9 rounded-full bg-white', tone.icon)}>
          <Icon className="h-[18px] w-[18px]" aria-hidden />
        </span>
        <div className="flex-1 min-w-[240px]">
          <p className="m-0 mb-1.5 font-head text-[15px] font-bold">{title}</p>
          <p className="m-0 text-[13px] leading-[1.6]">{body}</p>
        </div>
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className={cn(
              'inline-flex items-center flex-none min-h-[44px] px-4 bg-white border-[1.5px] rounded-lg font-head text-[12.5px] font-bold transition-colors duration-150 motion-reduce:transition-none hover:bg-white/60 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
              tone.btn,
            )}
          >
            {action.label}
          </button>
        )}
      </div>

      <div
        className={cn(
          'grid gap-3.5 mt-[18px] pt-4 border-t [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]',
          tone.grid,
        )}
      >
        {fields.map(([label, value]) => (
          <div key={label}>
            <span className="block font-head text-[9.5px] font-bold tracking-[.1em] uppercase opacity-70">
              {label}
            </span>
            <span className="block mt-[5px] font-head text-[13px] font-bold tabular-nums">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// «Sus números por período» — la métrica es la fila, el período la columna.
// Cápsulas de 38px, total en tinte teal, ceros apagados por RELLENO.
// ---------------------------------------------------------------------------

function PeriodTable({ card, lang }: { card: AgentCardView; lang: string }): JSX.Element {
  const t = useT();
  const p = card.periods;

  const monthName = new Date(`${p.monthStartNy}T12:00:00`).toLocaleDateString(lang === 'es' ? 'es' : 'en', {
    month: 'long',
  });
  const weekDay = Number.parseInt(p.weekStartNy.slice(8), 10) || 1;
  const totalSub = p.firstActivityAt
    ? t('f5r6a.p.totalSub', {
        d: new Date(p.firstActivityAt).toLocaleDateString(lang === 'es' ? 'es' : 'en', {
          day: 'numeric',
          month: 'short',
        }),
      })
    : t('f5r6a.p.totalSubNone');

  const cols: Array<{ label: string; sub: string }> = [
    { label: t('f5r6a.p.today'), sub: t('f5r6a.p.todaySub') },
    { label: t('f5r6a.p.week'), sub: t('f5r6a.p.weekSub', { d: weekDay }) },
    { label: t('f5r6a.p.month'), sub: monthName },
    { label: t('f5r6a.p.total'), sub: totalSub },
  ];

  const windows = [p.today, p.week, p.month, p.total];

  interface Cell {
    main: string;
    suffix?: string;
    zero: boolean;
  }
  const rows: Array<{ label: string; hint?: string; cells: Cell[] }> = [
    {
      label: t('f5r6a.m.screenings'),
      cells: windows.map((w) => ({ main: String(w.screenings), zero: w.screenings === 0 })),
    },
    {
      label: t('f5r6a.m.attempts'),
      hint: t('f5r6a.m.attemptsHint'),
      cells: windows.map((w) => ({ main: String(w.attempts), zero: w.attempts === 0 })),
    },
    {
      label: t('f5r6a.m.contacts'),
      hint: t('f5r6a.m.contactsHint'),
      cells: windows.map((w) => ({
        main: String(w.contactsAchieved),
        suffix: t('f5r6a.m.contactsOf', { n: w.contactsDenominator }),
        zero: w.contactsAchieved === 0,
      })),
    },
    {
      label: t('f5r6a.m.missed'),
      hint: t('f5r6a.m.missedHint'),
      cells: windows.map((w) => ({ main: String(w.missedBookings), zero: w.missedBookings === 0 })),
    },
    {
      label: t('f5r6a.m.callTime'),
      hint: t('f5r6a.m.callTimeHint'),
      cells: windows.map((w) => ({ main: fmtHM(w.callTimeSeconds), zero: w.callTimeSeconds === 0 })),
    },
    {
      label: t('f5r6a.m.activeTime'),
      hint: t('f5r6a.m.activeTimeHint'),
      cells: windows.map((w) => ({ main: fmtHM(w.activeWorkSeconds), zero: w.activeWorkSeconds === 0 })),
    },
    {
      label: t('f5r6a.m.noMedicaid'),
      hint: t('f5r6a.m.noMedicaidHint'),
      cells: windows.map((w) => ({ main: String(w.ineligibleClosed), zero: w.ineligibleClosed === 0 })),
    },
  ];

  return (
    <section data-testid="agent-stats-panel">
      <p className="m-0 mb-3.5 font-head text-[10.5px] font-bold tracking-[.14em] uppercase text-nav-teal">
        {t('f5r6a.table.kicker')}
      </p>
      <div className="border-[1.5px] border-nav-card rounded-xl overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse font-body">
            <thead>
              <tr>
                <th
                  scope="col"
                  className="h-[52px] px-4 py-2.5 w-[44%] text-left align-middle font-head text-[11px] font-bold tracking-[.06em] uppercase text-nav-slate border-b border-nav-hair bg-nav-softer"
                >
                  {t('f5r6a.table.metric')}
                </th>
                {cols.map((c, i) => (
                  <th
                    key={c.label}
                    scope="col"
                    className={cn(
                      'h-[52px] px-3.5 py-2.5 text-center align-middle whitespace-nowrap font-head text-[11px] font-bold tracking-[.06em] uppercase text-nav-navy border-b border-nav-hair',
                      i === 3 ? 'bg-[#f4fafa]' : 'bg-nav-softer',
                    )}
                  >
                    {c.label}
                    {/* El alcance ESCRITO bajo cada título — «Mes» y «Total»
                        ya no dicen lo mismo. */}
                    <span className="block mt-[3px] font-body text-[10px] font-medium tracking-normal normal-case text-nav-slate tabular-nums">
                      {c.sub}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.label}>
                  <td className="px-4 py-3.5 border-t border-nav-hair align-middle font-head text-[13px] font-bold text-nav-navy">
                    {m.label}
                    {m.hint && (
                      <span className="block mt-[3px] font-body text-[11.5px] font-normal leading-[1.45] text-nav-slate">
                        {m.hint}
                      </span>
                    )}
                  </td>
                  {m.cells.map((v, i) => {
                    const total = i === 3;
                    return (
                      <td
                        key={i}
                        className={cn(
                          'px-3.5 py-[11px] text-center border-t border-nav-hair align-middle whitespace-nowrap',
                          total ? 'bg-[#f4fafa]' : 'bg-white',
                        )}
                      >
                        <span
                          className={cn(
                            'inline-flex items-baseline justify-center gap-[5px] min-h-[38px] px-3 box-border rounded-[9px] border-[1.5px]',
                            v.suffix ? 'min-w-[88px]' : 'min-w-[62px]',
                            v.zero
                              ? 'border-nav-hair bg-nav-softer'
                              : total
                                ? 'border-[#c9e2e2] bg-[#e8f3f3]'
                                : 'border-nav-card bg-white',
                          )}
                        >
                          <span
                            className={cn(
                              'self-center font-head text-[16px] font-extrabold tracking-[-0.01em] leading-none tabular-nums',
                              v.zero ? 'text-nav-slate' : total ? 'text-nav-tealDark' : 'text-nav-navy',
                            )}
                          >
                            {v.main}
                          </span>
                          {v.suffix && (
                            <span
                              className={cn(
                                'self-center font-body text-[11.5px] font-semibold leading-none tabular-nums',
                                total ? 'text-nav-tealDark' : 'text-nav-slate',
                              )}
                            >
                              {v.suffix}
                            </span>
                          )}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// «Sus últimos screenings» — personas, no hashes; el estado en palabras.
// ---------------------------------------------------------------------------

function ScreeningsLog({
  card,
  rows,
  filter,
  onFilter,
  lang,
  onOpenScreening,
  onSeeAll,
}: {
  card: AgentCardView;
  rows: AgentCardScreening[];
  filter: LogFilter;
  onFilter: (f: LogFilter) => void;
  lang: string;
  onOpenScreening: (id: string) => void;
  onSeeAll: () => void;
}): JSX.Element {
  const t = useT();

  const filters: Array<{ key: LogFilter; label: string }> = [
    { key: 'all', label: t('f5r6a.log.filter.all') },
    { key: 'pending', label: t('f5r6a.log.filter.pending') },
    { key: 'flagged', label: t('f5r6a.log.filter.flagged') },
  ];

  const colHead = 'font-head text-[10.5px] font-bold tracking-[.1em] uppercase text-nav-slate';

  return (
    <section>
      <div className="flex items-baseline justify-between gap-3.5 flex-wrap mb-3.5">
        <p className="m-0 font-head text-[10.5px] font-bold tracking-[.14em] uppercase text-nav-teal">
          {t('f5r6a.log.kicker')}
        </p>
        <div className="flex gap-2 flex-wrap">
          {filters.map((f) => {
            const on = filter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                aria-pressed={on}
                onClick={() => onFilter(f.key)}
                className={cn(
                  'min-h-[44px] px-[13px] rounded-lg border-[1.5px] font-head text-[12px] font-bold transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
                  on
                    ? 'bg-nav-navy text-white border-nav-navy hover:bg-nav-navyHover hover:border-nav-navyHover'
                    : 'bg-white text-nav-navy border-nav-line hover:bg-[#f4fafa] hover:text-nav-tealDark hover:border-nav-teal',
                )}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="border-[1.5px] border-nav-card rounded-xl overflow-hidden">
        <div className="flex items-center gap-3 px-4 h-[38px] bg-nav-softer border-b border-nav-hair">
          <span className={cn(colHead, 'flex-1 min-w-[150px]')}>{t('f5r6a.log.col.member')}</span>
          <span className={cn(colHead, 'flex-none w-[88px] text-right')}>{t('f5r6a.log.col.duration')}</span>
          <span className={cn(colHead, 'flex-none w-[166px] text-right')}>{t('f5r6a.log.col.state')}</span>
          <span className="w-[74px] flex-none" />
        </div>

        {rows.length === 0 ? (
          <p className="m-0 px-4 py-5 text-[13px] text-nav-slate">
            {t('f5r6a.log.emptyFiltered', { n: card.recent.length })}
          </p>
        ) : (
          rows.map((r, i) => (
            <LogRow key={r.id} r={r} index={i} lang={lang} onOpen={() => onOpenScreening(r.id)} />
          ))
        )}

        <div className="flex items-center justify-between gap-3.5 flex-wrap px-4 py-[13px] border-t border-nav-hair bg-nav-softer">
          <p className="m-0 text-[12.5px] text-nav-slate tabular-nums">
            {t('f5r6a.log.page', { n: rows.length, total: card.logTotal })}
          </p>
          <button type="button" className={GHOST_BTN} onClick={onSeeAll}>
            {t('f5r6a.log.all')}
          </button>
        </div>
      </div>
    </section>
  );
}

function LogRow({
  r,
  index,
  lang,
  onOpen,
}: {
  r: AgentCardScreening;
  index: number;
  lang: string;
  onOpen: () => void;
}): JSX.Element {
  const t = useT();
  const [notesOpen, setNotesOpen] = useState(false);

  const when = ((): string => {
    if (!r.submittedAt) return '—';
    const d = new Date(r.submittedAt);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const yesterday = new Date(now.getTime() - 86_400_000).toDateString() === d.toDateString();
    const hm = hhmm(r.submittedAt, lang);
    if (sameDay) return t('f5r6a.log.today', { t: hm });
    if (yesterday) return t('f5r6a.log.yesterday', { t: hm });
    return `${d.toLocaleDateString(lang === 'es' ? 'es' : 'en', { day: 'numeric', month: 'short' })} ${hm}`;
  })();

  // Contexto de la fila: parentesco de hogar («hijo de Fabio Núñez» —
  // relationship_type ya viene en castellano de la base) o los dominios
  // positivos («sin necesidades» cuando 0).
  const context =
    r.isHousehold && r.householdRelation && r.householdOf
      ? t('f5r6a.log.hhOf', { rel: r.householdRelation, name: r.householdOf })
      : r.positiveDomains === 0
        ? t('f5r6a.log.noNeeds')
        : r.positiveDomains === 1
          ? t('f5r6a.log.positiveOne')
          : t('f5r6a.log.positives', { n: r.positiveDomains });

  const cinPart = r.cinMasked ? t('f5r6a.log.cin', { cin: r.cinMasked }) : t('f5r6a.log.cinNone');
  const meta = `${cinPart} · ${when} · ${context}`;

  const dur = ((): string => {
    if (r.totalDurationSec == null) return '—';
    const m = Math.floor(r.totalDurationSec / 60);
    const s = r.totalDurationSec % 60;
    return t('f5r6a.dur.ms', { m, s: String(s).padStart(2, '0') });
  })();

  // «Marcado con error», no «devuelto» — el circuito de devolución no existe
  // (doc del dibujo): la razón estructurada en palabras, con fallback legacy.
  const flagWord = ((): string => {
    switch (r.flagReason) {
      case 'no_medicaid':
        return t('f5r6a.flag.no_medicaid');
      case 'cin_invalid':
        return t('f5r6a.flag.cin_invalid');
      case 'data_inconsistent':
        return t('f5r6a.flag.data_inconsistent');
      case 'duplicate':
        return t('f5r6a.flag.duplicate');
      case 'other':
        return r.flagReasonText || t('f5r6a.flag.other');
      default:
        return r.legacyFlagText || t('f5r6a.flag.other');
    }
  })();

  const stateLabel =
    r.findhelpStatus === 'uploaded'
      ? t('f5r6a.log.state.uploaded')
      : r.findhelpStatus === 'flagged'
        ? t('f5r6a.log.state.flagged', { reason: flagWord })
        : t('f5r6a.log.state.pending');

  const stateColor =
    r.findhelpStatus === 'flagged'
      ? 'text-nav-red'
      : r.findhelpStatus === 'pending'
        ? 'text-nav-amberText'
        : 'text-nav-sageText';

  return (
    <div
      data-testid="admin-recent-screening-row"
      style={{ animationDelay: `${index * 0.04}s` }}
      className={cn(
        'flex items-center gap-3 flex-wrap min-h-[64px] px-4 py-2.5 bg-white animate-fade-up motion-reduce:animate-none',
        index > 0 && 'border-t border-nav-hair',
      )}
    >
      {/* CONSERVA E-23: la fila sigue abriendo el detalle read-only del
          screening — ahora desde el bloque del miembro (sin botones anidados). */}
      <button
        type="button"
        onClick={onOpen}
        aria-label={t('f5r6a.log.openDetail', { name: r.memberName })}
        className="flex-1 min-w-[150px] text-left rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal hover:bg-nav-tealTint/40 transition-colors duration-150 motion-reduce:transition-none px-1 -mx-1 py-0.5"
      >
        <span className="flex items-center gap-2 flex-wrap">
          <span className="font-head text-[13.5px] font-bold">{r.memberName}</span>
          {r.isHousehold && (
            <span className="bg-nav-tealTint text-nav-tealDark border border-[#c9e2e2] rounded-full px-2 py-0.5 font-head text-[9.5px] font-bold tracking-[.04em] uppercase">
              {t('f5r6a.log.hh')}
            </span>
          )}
        </span>
        <span className="block mt-[3px] text-[12px] text-nav-slate tabular-nums">{meta}</span>
      </button>

      <span className="flex-none w-[88px] text-right font-head text-[13px] font-bold text-nav-slate tabular-nums">
        {dur}
      </span>
      <span className={cn('flex-none w-[166px] text-right font-head text-[12px] font-bold', stateColor)}>
        {stateLabel}
      </span>

      {r.notesCount > 0 ? (
        <button
          type="button"
          onClick={() => setNotesOpen((v) => !v)}
          aria-expanded={notesOpen}
          aria-label={
            r.notesCount === 1
              ? t('f5r6a.log.notesAriaOne', { name: r.memberName })
              : t('f5r6a.log.notesAria', { n: r.notesCount, name: r.memberName })
          }
          className="inline-flex items-center justify-center gap-1.5 flex-none w-[74px] min-h-[44px] bg-white text-nav-navy border-[1.5px] border-nav-line rounded-lg font-head text-[12px] font-bold tabular-nums transition-colors duration-150 motion-reduce:transition-none hover:bg-[#f4fafa] hover:border-nav-teal hover:text-nav-tealDark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
        >
          <FileText className="h-3.5 w-3.5 flex-none" aria-hidden />
          {r.notesCount}
        </button>
      ) : (
        <span className="flex-none w-[74px] text-center font-body text-[12px] text-nav-faint">
          {t('f5r6a.log.noNotes')}
        </span>
      )}

      {notesOpen && r.notes.length > 0 && (
        <div className="basis-full mt-1 mb-1.5 space-y-2">
          {r.notes.map((n, i) => (
            <StickyNoteCard
              key={i}
              domain={n.domain}
              text={n.text}
              capturedAt={r.submittedAt ?? undefined}
              size="compact"
            />
          ))}
        </div>
      )}
    </div>
  );
}
